// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { CopyToClipboard, Modal, Table } from '@awsui/components-react';

import '@awsui/global-styles/index.css';

const MULTI_MEETING_SUMMARY = `Please provide one mega summary across all meetings. 
The mega summary format needs to be in the format of Key Takeaways, Detailed Findings and Recommendations as per meeting.
If there are multiple meetings, please combine them together into one summary.`;

const KEY_TAKEAWAYS = `Please write the main findings, themes, sentiments, pain points, and 
  expectations from the sessions in the below format without any without preamble or 
  additional explanation. Use gender neutral pronouns.`;

const DETAILED_FINDINGS = `Please elaborate on themes, sentiments, pain points, and expectations with quotes/examples.
[Short list of no more than 5 of the main topics covered in the meeting.]

Please present the output in a clean, organized, and easy-to-read Markdown format without preamble or additional explanation.`;

const RECOMMENDATIONS = `Based on the meeting details, extract a list of actionable recommendations based on findings for improvement and addressing pain points for the product being discussed. 
The recommendations should be focus on the product usage experience. Please present the output in the following Markdown format: 

**1.[Concise description of the actionable recommendations**]

[Longer description of the actionable recommendations]

**2. [Concise description of the next actionable recommendation]**

[Longer description of the actionable recommendation]


Please present, the actionable recommendations in a clean, organized, and easy-to-read Markdown format without preamble or additional explanation.`;

const KEY_TOPICS = `Please identify the top 5 main topics covered in the meetings and provide a brief description for each topic.`;

const ANECDOTES = `Identify anecdotes related to [topic]. Return verbatim quotes. Do not modify the original text in your response`;

const PROMPTS = [
  {
    action: 'Multi-Meeting Summary',
    description: 'This is the prompt when you click on the "Summarize" button in the Meeting Query Tool',
    text: MULTI_MEETING_SUMMARY,
  },
  {
    action: 'Key Takeaways',
    description: 'This prompt is used when generating the "Key Takeaways" section for a single meeting summary.',
    text: KEY_TAKEAWAYS,
  },
  {
    action: 'Detailed Findings',
    description: 'This prompt is used when generating the "Detailed Findings" section for a single meeting summary.',
    text: DETAILED_FINDINGS,
  },
  {
    action: 'Recommendations',
    description: 'This prompt is used when generating the "Recommendations" section for a single meeting summary.',
    text: RECOMMENDATIONS,
  },
  {
    action: 'Key Topics',
    description: 'This prompt is used to find the top 5 key topics across the meetings',
    text: KEY_TOPICS,
  },
  {
    action: 'Anecdotes',
    description: 'This prompt is used to find the anecdotes across the meetings',
    text: ANECDOTES,
  },
];

// eslint-disable-next-line react/prop-types
const CopyPrompt = ({ text }) => {
  return <CopyToClipboard textToCopy={text} copySuccessText="Prompt Copied" copyErrorText="Error copying Prompt" />;
};

/**
 * This is a model that shows a collection of prompts customers can use for prompting the LLM
 * This is a table that displays the prompts in a list and enables users to copy the values
 * @returns {React.JSX.Element}
 * @constructor
 */
const PromptDetails = () => {
  return (
    <Table
      columnDefinitions={[
        {
          id: 'action',
          header: 'Action',
          cell: (item) => item.action,
        },
        {
          id: 'description',
          header: 'Description',
          cell: (item) => item.description,
        },
        {
          id: 'text',
          header: 'Text',
          cell: (item) => item.text,
        },
        {
          id: 'copy',
          header: 'Copy',
          cell: (item) => CopyPrompt(item),
        },
      ]}
      wrapLines
      items={PROMPTS}
    />
  );
};

/* eslint-disable react/prop-types */
export const PromptModal = ({ setVisible, visible }) => {
  return (
    <Modal size="max" onDismiss={() => setVisible(false)} visible={visible} header="Prompts">
      <PromptDetails />
    </Modal>
  );
};

export default PromptModal;
